// AI 牌友角色設定：每桌三位、三桌共九位，個性對應座位的打法（下家進攻、對家平衡、上家謹慎）
// 對應 docs/PRD-Freemium.md 第 8.1 節、docs/Characters.md（角色設定與台詞說明）
//
// - 新手桌：和氣的鄰居；進階桌：常來的牌友；高手桌：老牌精
// - 語言以國語為主，夾台語口頭禪
// - 台詞依事件分類；{name} 會換成玩家稱呼、{days} 換成天數、{hand} 換成牌型名稱

import { TableId, type TTableId } from './table-catalog';
import { Seat, type TSeat } from './types';

export const CharacterLine = {
  Greet: 'greet', // 開局打招呼
  GreetStreak: 'greet-streak', // 玩家連續好幾天來
  GreetReturn: 'greet-return', // 玩家好幾天沒來
  GreetBigHand: 'greet-big-hand', // 記得玩家上次胡的大牌
  Meld: 'meld', // 自己吃碰槓
  SelfDraw: 'self-draw', // 自己自摸
  Win: 'win', // 自己胡別人的牌
  DealIn: 'deal-in', // 自己放槍
  PlayerWin: 'player-win', // 玩家胡牌
  Draw: 'draw', // 流局
} as const;
export type TCharacterLine = (typeof CharacterLine)[keyof typeof CharacterLine];

export interface ICharacter {
  readonly id: string;
  readonly name: string;
  readonly tableId: TTableId;
  readonly seat: TSeat; // 下家 1（進攻）、對家 2（平衡）、上家 3（謹慎）
  readonly personality: string;
  readonly avatar: string; // public/ 底下的頭像路徑（Codex 產圖）
  readonly lines: Readonly<Record<TCharacterLine, readonly string[]>>;
}

const avatarPath = (id: string): string => `assets/avatars/${id}.png`;

export const CHARACTERS: readonly ICharacter[] = [
  // ---- 新手桌：和氣的鄰居 ----
  {
    id: 'beginner-1',
    name: '阿財伯',
    tableId: TableId.Beginner,
    seat: Seat.Right,
    personality: '熱心的老鄰居，看到能碰就想碰，輸了也哈哈大笑',
    avatar: avatarPath('beginner-1'),
    lines: {
      greet: ['來來來，坐坐坐！今天輕鬆打', '欸，{name}來了喔，快開始'],
      'greet-streak': ['連續 {days} 天來報到，比我還勤快！'],
      'greet-return': ['{days} 天沒看到你，還以為你搬家了'],
      'greet-big-hand': ['上次你那個{hand}，我到現在還在想'],
      meld: ['碰！先碰再說啦', '這張我要了', '吃一下，不要客氣'],
      'self-draw': ['自摸！今天手氣有夠好', '哈哈，自己摸到了'],
      win: ['胡啦！謝謝你喔', '就等你這張'],
      'deal-in': ['哎唷，被抓到了', '沒關係沒關係，下把再來'],
      'player-win': ['讚喔！打得真好', '欸你很會喔'],
      draw: ['流局，大家都沒輸，好啦'],
    },
  },
  {
    id: 'beginner-2',
    name: '阿花姨',
    tableId: TableId.Beginner,
    seat: Seat.Opposite,
    personality: '和氣的鄰居阿姨，常常鼓勵別人，打牌像在聊天',
    avatar: avatarPath('beginner-2'),
    lines: {
      greet: ['慢慢來，免緊張', '今天吃飽沒？來打一圈'],
      'greet-streak': ['你連續來 {days} 天了，真乖'],
      'greet-return': ['好幾天沒看到你，阿姨很想你'],
      'greet-big-hand': ['上次你胡{hand}，阿姨都替你高興'],
      meld: ['碰一下', '這張剛好'],
      'self-draw': ['自摸！不好意思喔', '摸到了，運氣啦'],
      win: ['胡了，承讓承讓', '這把我的'],
      'deal-in': ['唉呀，算我的', '打錯了啦'],
      'player-win': ['好厲害！', '恭喜恭喜'],
      draw: ['流局了，喝口茶再來'],
    },
  },
  {
    id: 'beginner-3',
    name: '美玉姊',
    tableId: TableId.Beginner,
    seat: Seat.Left,
    personality: '小心謹慎，出牌前總要想一下',
    avatar: avatarPath('beginner-3'),
    lines: {
      greet: ['我打比較慢，大家多包涵', '今天要小心打'],
      'greet-streak': ['{days} 天都有來，好認真'],
      'greet-return': ['你好久沒來了，牌技有沒有生疏'],
      'greet-big-hand': ['聽說你上次胡了{hand}，好羨慕'],
      meld: ['那我碰好了', '嗯…這張我要'],
      'self-draw': ['自摸，我也嚇一跳', '摸到了耶'],
      win: ['胡…胡了嗎？對，胡了', '不好意思，我胡'],
      'deal-in': ['我就知道這張危險', '早知道就不打'],
      'player-win': ['你好會算喔', '佩服佩服'],
      draw: ['流局，還好沒放槍'],
    },
  },
  // ---- 進階桌：常來的牌友 ----
  {
    id: 'intermediate-1',
    name: '金水伯',
    tableId: TableId.Intermediate,
    seat: Seat.Right,
    personality: '愛鬥嘴的老牌友，嘴巴不饒人但很講義氣',
    avatar: avatarPath('intermediate-1'),
    lines: {
      greet: ['欸，又來送錢喔', '今天不要哭著回家'],
      'greet-streak': ['{days} 天沒缺席，你是住在這喔'],
      'greet-return': ['唷，{days} 天沒來，是怕了喔'],
      'greet-big-hand': ['上次{hand}給你矇到，這次沒那麼好運'],
      meld: ['碰！別想跑', '這張就是我的'],
      'self-draw': ['自摸！欸你就沒輸', '自己來啦，拿錢拿錢'],
      win: ['胡！就等你這張', '謝謝喔，剛好'],
      'deal-in': ['哼，算你厲害', '這張也胡喔？'],
      'player-win': ['好啦好啦，這把給你', '欸，你偷練喔'],
      draw: ['流局，算你們好運'],
    },
  },
  {
    id: 'intermediate-2',
    name: '秀琴阿姨',
    tableId: TableId.Intermediate,
    seat: Seat.Opposite,
    personality: '愛聊天的牌友，打牌之餘最愛講八卦',
    avatar: avatarPath('intermediate-2'),
    lines: {
      greet: ['今天手氣不錯喔，我感覺得到', '來，邊打邊聊'],
      'greet-streak': ['連續 {days} 天來，比上班還準時'],
      'greet-return': ['你這幾天跑去哪裡玩，都不揪'],
      'greet-big-hand': ['上次你胡{hand}，我跟大家講了好幾遍'],
      meld: ['碰，謝謝喔', '這張我收下'],
      'self-draw': ['自摸！今天穿紅色有效', '摸到了，開心'],
      win: ['胡了，請客請客', '剛好聽這張'],
      'deal-in': ['唉唷，聊天聊到忘記', '被你抓到了'],
      'player-win': ['哇，好漂亮的牌', '今天你最旺'],
      draw: ['流局了，大家平安'],
    },
  },
  {
    id: 'intermediate-3',
    name: '老陳',
    tableId: TableId.Intermediate,
    seat: Seat.Left,
    personality: '話不多的穩健派，最討厭放槍',
    avatar: avatarPath('intermediate-3'),
    lines: {
      greet: ['穩穩打，別放槍', '開始吧'],
      'greet-streak': ['{days} 天了，有恆心'],
      'greet-return': ['回來了，好'],
      'greet-big-hand': ['{hand}那把，打得漂亮'],
      meld: ['碰', '吃'],
      'self-draw': ['自摸', '來了'],
      win: ['胡', '這張'],
      'deal-in': ['失誤', '唉'],
      'player-win': ['不錯', '有一套'],
      draw: ['流局，正常'],
    },
  },
  // ---- 高手桌：老牌精 ----
  {
    id: 'expert-1',
    name: '麻將阿嬤',
    tableId: TableId.Expert,
    seat: Seat.Right,
    personality: '打了五十年牌的老牌精，出手快狠準',
    avatar: avatarPath('expert-1'),
    lines: {
      greet: ['少年仔，坐好喔', '阿嬤今天手很癢'],
      'greet-streak': ['{days} 天都來找阿嬤，乖孫'],
      'greet-return': ['{days} 天沒來，阿嬤等你湊一桌'],
      'greet-big-hand': ['上次{hand}，阿嬤年輕時也胡過'],
      meld: ['碰！慢慢來啦', '這張阿嬤要'],
      'self-draw': ['自摸！三台起跳', '阿嬤自己來'],
      win: ['胡！你太嫩了', '就知道你會打這張'],
      'deal-in': ['嘖，看走眼', '阿嬤也有失手的時候'],
      'player-win': ['不錯喔，有阿嬤的樣子', '這把阿嬤服氣'],
      draw: ['流局，下把見真章'],
    },
  },
  {
    id: 'expert-2',
    name: '林老師',
    tableId: TableId.Expert,
    seat: Seat.Opposite,
    personality: '退休數學老師，每張牌都要算機率',
    avatar: avatarPath('expert-2'),
    lines: {
      greet: ['牌要算，不是用猜的', '今天來上一課'],
      'greet-streak': ['連續 {days} 天，學習態度很好'],
      'greet-return': ['{days} 天沒來，功課要補回來'],
      'greet-big-hand': ['{hand}的機率很低，你上次很幸運'],
      meld: ['碰，這樣聽牌比較快', '吃，有效張數多四張'],
      'self-draw': ['自摸，機率站在我這邊', '算好的'],
      win: ['胡，跟我算的一樣', '這張剩一張，被我等到了'],
      'deal-in': ['算漏了一家', '這是機率的代價'],
      'player-win': ['算得好，給你一百分', '這把你贏得漂亮'],
      draw: ['流局，在預期之內'],
    },
  },
  {
    id: 'expert-3',
    name: '吳教官',
    tableId: TableId.Expert,
    seat: Seat.Left,
    personality: '退休教官，防守滴水不漏，一張都不放',
    avatar: avatarPath('expert-3'),
    lines: {
      greet: ['一張都不會讓你', '坐正，開始'],
      'greet-streak': ['{days} 天全勤，嘉獎一次'],
      'greet-return': ['{days} 天沒報到，記曠課'],
      'greet-big-hand': ['{hand}？這次不會讓你得逞'],
      meld: ['碰，收', '吃，執行'],
      'self-draw': ['自摸，任務完成', '收工'],
      win: ['胡，抓到了', '就等這張'],
      'deal-in': ['防線失守', '這張是我疏忽'],
      'player-win': ['打得好，有紀律', '這把你贏，我服'],
      draw: ['流局，防守成功'],
    },
  },
];

export const charactersForTable = (tableId: unknown): readonly ICharacter[] =>
  CHARACTERS.filter((c) => c.tableId === tableId);

export const characterAt = (tableId: unknown, seat: TSeat): ICharacter | undefined =>
  CHARACTERS.find((c) => c.tableId === tableId && c.seat === seat);

/** 套用台詞變數；random 由呼叫端注入（測試可重現）。 */
export const pickLine = (
  character: ICharacter,
  line: TCharacterLine,
  random: () => number,
  vars: Readonly<Record<string, string | number>> = {}
): string => {
  const options = character.lines[line];
  const raw = options[Math.floor(random() * options.length)] ?? '';
  return raw.replace(/\{(\w+)\}/g, (_, key: string) => String(vars[key] ?? ''));
};
